if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.isLoading = false;
        this.isVisible = false;
        this.observer = null;
        this.unsubscribeVariantChange = null;
        this.unsubscribeCartUpdate = null;
        this.unsubscribeCartError = null;
      }

      connectedCallback() {
        this.mainProductInfo = document.querySelector('product-info[id^="MainProduct"]');
        if (!this.mainProductInfo) return;

        this.mainSectionId = this.mainProductInfo.dataset.section;
        this.mainForm = this.mainProductInfo.querySelector('product-form form');
        this.mainSubmitButton = this.mainProductInfo.querySelector('.product-form__submit');

        if (!this.mainSubmitButton) return;

        this.stickyButton = this.querySelector('.paw-sticky-atc__button');
        this.stickyButtonText = this.querySelector('.paw-sticky-atc__button-text');
        this.stickySpinner = this.querySelector('.paw-sticky-atc__spinner');
        this.stickyPrice = this.querySelector('.paw-sticky-atc__price');
        this.stickyImage = this.querySelector('.paw-sticky-atc__image');
        this.stickyVariant = this.querySelector('.paw-sticky-atc__variant');
        this.stickyInfo = this.querySelector('.paw-sticky-atc__info');

        this.stickyButton.addEventListener('click', this.onAddClick.bind(this));
        this.setupObserver();

        this.unsubscribeVariantChange = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.onVariantChange.bind(this)
        );
        this.unsubscribeCartUpdate = subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          this.onCartUpdate.bind(this)
        );
        this.unsubscribeCartError = subscribe(
          PUB_SUB_EVENTS.cartError,
          this.onCartError.bind(this)
        );
      }

      setupObserver() {
        const headerHeight = parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0',
          10
        );

        this.observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              this.hide();
            } else {
              const rootTop = entry.rootBounds ? entry.rootBounds.top : headerHeight;
              if (entry.boundingClientRect.bottom <= rootTop) {
                this.show();
              } else {
                this.hide();
              }
            }
          },
          {
            rootMargin: `-${headerHeight}px 0px 0px 0px`,
            threshold: 0,
          }
        );

        this.observer.observe(this.mainSubmitButton);
      }

      show() {
        if (this.isVisible) return;
        this.isVisible = true;
        this.classList.add('paw-sticky-atc--visible');
        requestAnimationFrame(() => {
          document.body.style.paddingBottom = `${this.offsetHeight}px`;
        });
      }

      hide() {
        if (!this.isVisible) return;
        this.isVisible = false;
        this.classList.remove('paw-sticky-atc--visible');
        document.body.style.paddingBottom = '';
      }

      onAddClick() {
        if (this.isLoading) return;
        if (this.stickyButton.disabled) return;

        const mainButton = this.mainProductInfo.querySelector('.product-form__submit');
        if (mainButton && mainButton.getAttribute('aria-disabled') === 'true') return;

        this.setLoading(true);

        if (this.mainForm) {
          this.mainForm.requestSubmit();
        }
      }

      setLoading(loading) {
        this.isLoading = loading;
        if (loading) {
          this.stickyButton.setAttribute('aria-disabled', 'true');
          this.stickySpinner.style.display = '';
          this.stickyButtonText.style.visibility = 'hidden';
        } else {
          this.stickyButton.removeAttribute('aria-disabled');
          this.stickySpinner.style.display = 'none';
          this.stickyButtonText.style.visibility = '';
        }
      }

      onVariantChange({ data }) {
        const { sectionId, html, variant } = data;
        if (sectionId !== this.mainSectionId) return;

        this.syncPrice(html, sectionId);
        this.syncAvailability(html, sectionId, variant);
        this.syncVariantLabel(variant);
        this.syncImage(variant);
      }

      syncPrice(html, sectionId) {
        const priceSource = html.getElementById(`price-${sectionId}`);
        if (!priceSource || !this.stickyPrice) return;

        const onSale = priceSource.querySelector('.price')?.classList.contains('price--on-sale');

        if (onSale) {
          const salePrice = priceSource.querySelector('.price__sale .price-item--sale');
          const comparePrice = priceSource.querySelector('.price__sale s.price-item--regular');

          this.stickyPrice.innerHTML =
            '<span class="paw-sticky-atc__current-price paw-sticky-atc__current-price--sale">' +
              (salePrice ? salePrice.textContent.trim() : '') +
            '</span>' +
            (comparePrice
              ? '<span class="paw-sticky-atc__compare-price">' + comparePrice.textContent.trim() + '</span>'
              : '');
        } else {
          const regularPrice = priceSource.querySelector('.price__regular .price-item--regular');

          this.stickyPrice.innerHTML =
            '<span class="paw-sticky-atc__current-price">' +
              (regularPrice ? regularPrice.textContent.trim() : '') +
            '</span>';
        }
      }

      syncAvailability(html, sectionId, variant) {
        if (!this.stickyButton || !this.stickyButtonText) return;

        if (!variant) {
          this.stickyButton.disabled = true;
          this.stickyButtonText.textContent = window.variantStrings.unavailable;
          this.stickyButton.setAttribute('aria-label', window.variantStrings.unavailable);
          return;
        }

        const submitButton = html.getElementById(`ProductSubmitButton-${sectionId}`);
        const isDisabled = submitButton?.hasAttribute('disabled') ?? true;

        if (!variant.available || isDisabled) {
          this.stickyButton.disabled = true;
          this.stickyButtonText.textContent = window.variantStrings.soldOut;
          this.stickyButton.setAttribute('aria-label', window.variantStrings.soldOut);
        } else {
          this.stickyButton.disabled = false;
          this.stickyButtonText.textContent = window.variantStrings.addToCart;
          this.stickyButton.setAttribute('aria-label', window.variantStrings.addToCart);
        }
      }

      syncVariantLabel(variant) {
        if (!this.stickyVariant) return;

        if (variant && variant.title && variant.title !== 'Default Title') {
          this.stickyVariant.textContent = variant.title;
          this.stickyVariant.style.display = '';
        } else {
          this.stickyVariant.style.display = 'none';
        }
      }

      syncImage(variant) {
        if (!this.stickyImage) return;

        const media = variant?.featured_media;
        if (media?.preview_image?.src) {
          const src = media.preview_image.src;
          const separator = src.includes('?') ? '&' : '?';
          this.stickyImage.src = src + separator + 'width=112';
          this.stickyImage.alt = media.alt || '';
        }
      }

      onCartUpdate() {
        this.setLoading(false);
      }

      onCartError() {
        this.setLoading(false);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.unsubscribeVariantChange?.();
        this.unsubscribeCartUpdate?.();
        this.unsubscribeCartError?.();
        document.body.style.paddingBottom = '';
      }
    }
  );
}
